import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ColorPalette, DEFAULT_COLOR_PALETTE, ReportType } from '../../models/end-client-reporting-package';
import { applyDeckTheme } from './deck-theme';
import { MODULE_GUIDE, ModuleGuide } from './module-guide';
import { exampleBlock, guideSample, hasLiveExample, sampleFilter } from './module-guide-sample';
import {
  AggregateTable, ChartSeries, StudyFacts, aggregateAll, applyFilters, seriesForBlock, studyFacts,
} from './report-aggregation';
import {
  REPORT_BLOCK_GUIDANCE, REPORT_BLOCK_LABELS, groupedReportTypes,
} from './report-blocks/report-block-registry';
import type { PageSection } from '../../models/end-client-reporting-package';

/** Every module, grouped as the picker groups them -- the stage explainer included, marked. */
interface GuideGroup {
  label: string;
  types: ReportType[];
}

/** The explainer is part of a stage matrix: described here, but never added on its own. */
const NOT_ADDABLE: ReadonlySet<ReportType> = new Set<ReportType>(['stage-explainer']);

/**
 * The module guide: every module in a side list, and for the one picked, what it is for, what it
 * needs, where it misleads -- and a live example of it, drawn by the real module from a small
 * invented study, in the colours of the report it was opened from.
 *
 * Opened from a module's settings. When it was, "Use this module" switches that module to the one
 * being read about, so choosing and learning are the same motion.
 */
@Component({
  selector: 'app-module-guide-modal',
  standalone: false,
  template: `
    <div #root class="guide ecr-theme">
      <header class="guide-head">
        <div>
          <p class="guide-eyebrow mb-0">Report modules</p>
          <h2 id="guide-title" class="guide-title mb-0">Module guide</h2>
        </div>
        <button type="button" class="guide-close ml-auto" aria-label="Close the module guide" (click)="close()">
          <i class="fas fa-xmark" aria-hidden="true"></i>
        </button>
      </header>

      <div class="guide-body">
        <nav class="guide-nav" aria-label="Modules" (keydown)="onNavKeydown($event)">
          <div class="guide-search">
            <i class="fas fa-magnifying-glass" aria-hidden="true"></i>
            <input type="search" aria-label="Search the modules" placeholder="Search modules…"
                   [value]="query" (input)="query = $any($event.target).value; refresh()">
          </div>
          @for (group of visibleGroups; track group.label) {
            <p class="guide-group">{{ group.label }}</p>
            <ul class="list-unstyled mb-2">
              @for (type of group.types; track type) {
                <li>
                  <button type="button" class="guide-item" data-guide-item
                          [class.is-selected]="type === selected"
                          [attr.aria-current]="type === selected ? 'true' : null"
                          (click)="select(type)">
                    <span class="flex-fill">{{ labels[type] }}</span>
                    @if (type === current) {
                      <span class="guide-badge">In use</span>
                    }
                  </button>
                </li>
              }
            </ul>
          } @empty {
            <p class="guide-empty">No module matches “{{ query }}”.</p>
          }
        </nav>

        <div #main class="guide-main" role="region" tabindex="-1" aria-labelledby="guide-module">
          <p class="guide-eyebrow mb-1">{{ groupOf(selected) }}</p>
          <div class="d-flex align-items-start mb-2">
            <h3 id="guide-module" class="guide-module mb-0">{{ labels[selected] }}</h3>
            @if (choose && selected !== current && addable(selected)) {
              <button type="button" class="guide-use ml-auto" (click)="use()">
                <i class="fas fa-check mr-1" aria-hidden="true"></i>Use this module
              </button>
            } @else if (selected === current) {
              <span class="guide-badge is-large ml-auto">This module is in use</span>
            }
          </div>
          <p class="guide-lead">{{ guidance[selected] }}</p>

          <section class="guide-stage" aria-label="Example">
            <div class="guide-stage-head">
              <span class="guide-stage-label">Example</span>
              <span class="guide-stage-note">A sample coffee-shop study, drawn in this report's colours</span>
            </div>
            @if (live) {
              <div class="guide-card">
                <app-report-block-host [block]="block!"
                                       [series]="series"
                                       [palette]="palette"
                                       [studyFacts]="facts ?? undefined">
                </app-report-block-host>
              </div>
            } @else {
              <!-- A heatmap is the one module invented data cannot draw: it needs the image the
                   survey showed. An illustration of the idea stands in. -->
              <div class="guide-card guide-illustration">
                <svg viewBox="0 0 480 220" role="img" aria-label="Illustration: marks shaded over an image">
                  <defs>
                    <radialGradient id="guide-hot" cx="50%" cy="50%" r="50%">
                      <stop offset="0%" [attr.stop-color]="palette.negative" stop-opacity="0.85" />
                      <stop offset="100%" [attr.stop-color]="palette.negative" stop-opacity="0" />
                    </radialGradient>
                    <radialGradient id="guide-warm" cx="50%" cy="50%" r="50%">
                      <stop offset="0%" [attr.stop-color]="palette.accent" stop-opacity="0.75" />
                      <stop offset="100%" [attr.stop-color]="palette.accent" stop-opacity="0" />
                    </radialGradient>
                  </defs>
                  <rect x="20" y="16" width="440" height="188" rx="10" fill="var(--ecr-bg, #eef3f5)" />
                  <rect x="44" y="40" width="160" height="20" rx="4" fill="var(--ecr-grey, #c3ced3)" />
                  <rect x="44" y="74" width="230" height="10" rx="3" fill="var(--ecr-line, #dbe4e8)" />
                  <rect x="44" y="94" width="200" height="10" rx="3" fill="var(--ecr-line, #dbe4e8)" />
                  <rect x="300" y="40" width="130" height="130" rx="8" fill="var(--ecr-line, #dbe4e8)" />
                  <rect x="44" y="140" width="110" height="34" rx="17" [attr.fill]="palette.primary" />
                  <ellipse cx="365" cy="100" rx="70" ry="56" fill="url(#guide-hot)" />
                  <ellipse cx="100" cy="157" rx="62" ry="34" fill="url(#guide-warm)" />
                  <ellipse cx="124" cy="50" rx="74" ry="24" fill="url(#guide-warm)" opacity="0.6" />
                </svg>
                <p class="guide-stage-note mb-0 mt-2">Drawn over the survey's own image when the study has an image-highlighter question.</p>
              </div>
            }
          </section>

          @if (guide; as g) {
            <div class="guide-facts">
              <section>
                <h4 class="guide-h4"><i class="fas fa-bullseye" aria-hidden="true"></i>Best for</h4>
                <ul class="guide-list">
                  @for (item of g.bestFor; track item) { <li>{{ item }}</li> }
                </ul>
              </section>
              <section>
                <h4 class="guide-h4"><i class="fas fa-database" aria-hidden="true"></i>What it needs</h4>
                <p>{{ g.needs }}</p>
              </section>
              <section>
                <h4 class="guide-h4"><i class="fas fa-lightbulb" aria-hidden="true"></i>Tips</h4>
                <ul class="guide-list">
                  @for (item of g.tips; track item) { <li>{{ item }}</li> }
                </ul>
              </section>
              <section class="guide-watch">
                <h4 class="guide-h4"><i class="fas fa-triangle-exclamation" aria-hidden="true"></i>Watch out</h4>
                <p>{{ g.watchOut }}</p>
              </section>
            </div>

            @if (g.related.length) {
              <div class="guide-related">
                <span class="guide-stage-label mr-2">Compare with</span>
                @for (type of g.related; track type) {
                  <button type="button" class="guide-chip" (click)="select(type)">{{ labels[type] }}</button>
                }
              </div>
            }
          }

          @if (!addable(selected)) {
            <p class="guide-note"><i class="fas fa-circle-info mr-1" aria-hidden="true"></i>Added from a Stage Matrix's settings rather than on its own.</p>
          }
        </div>
      </div>
    </div>
  `,
  styles: [`
    :host { display: block; }
    .guide { display: flex; flex-direction: column; height: min(86vh, 820px); color: var(--ecr-ink, #26414c); }

    .guide-head {
      display: flex; align-items: center; padding: 16px 20px;
      border-bottom: 1px solid var(--ecr-line, #dbe4e8);
    }
    .guide-eyebrow {
      font-size: var(--ecr-font-micro, 9px); font-weight: 700; letter-spacing: 0.1em;
      text-transform: uppercase; color: var(--ecr-mut, #5a6b73);
    }
    .guide-title { font-size: 18px; font-weight: 700; color: var(--ecr-primary, #13455b); }
    .guide-close {
      width: 32px; height: 32px; border: 0; border-radius: 50%;
      color: var(--ecr-mut, #5a6b73); background: transparent;
    }
    .guide-close:hover, .guide-close:focus-visible { background: var(--ecr-bg, #eef3f5); color: var(--ecr-ink, #26414c); }

    .guide-body { display: flex; flex: 1 1 auto; min-height: 0; }

    .guide-nav {
      flex: 0 0 240px; overflow-y: auto; padding: 12px;
      border-right: 1px solid var(--ecr-line, #dbe4e8); background: var(--ecr-bg, #f4f7f8);
    }
    .guide-search { position: relative; margin-bottom: 10px; }
    .guide-search i { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); font-size: 12px; color: var(--ecr-mut, #5a6b73); }
    .guide-search input {
      width: 100%; padding: 6px 10px 6px 30px; border: 1px solid var(--ecr-line, #dbe4e8); border-radius: 999px;
      font-size: var(--ecr-font-sm, 12px); background: var(--ecr-card, #fff);
    }
    .guide-search input:focus { outline: 2px solid var(--ecr-primary, #13455b); outline-offset: 1px; }
    .guide-group {
      margin: 12px 8px 4px; font-size: var(--ecr-font-xs, 11px); font-weight: 700; letter-spacing: 0.06em;
      text-transform: uppercase; color: var(--ecr-mut, #5a6b73);
    }
    .guide-item {
      display: flex; align-items: center; width: 100%; padding: 6px 10px;
      border: 0; border-left: 3px solid transparent; border-radius: 0 6px 6px 0;
      font-size: var(--ecr-font-base, 13px); text-align: left; color: var(--ecr-ink, #26414c); background: transparent;
    }
    .guide-item:hover { background: var(--ecr-card, #fff); }
    .guide-item:focus-visible { outline: 2px solid var(--ecr-primary, #13455b); outline-offset: -2px; }
    .guide-item.is-selected {
      font-weight: 600; color: var(--ecr-primary, #13455b);
      border-left-color: var(--ecr-primary, #13455b); background: var(--ecr-primary-wash, #e6eef2);
    }
    .guide-badge {
      padding: 1px 6px; border-radius: 999px; font-size: 10px; font-weight: 600;
      color: var(--ecr-on-primary, #fff); background: var(--ecr-primary, #13455b);
    }
    .guide-badge.is-large { padding: 4px 10px; font-size: var(--ecr-font-xs, 11px); }
    .guide-empty { padding: 8px; font-size: var(--ecr-font-sm, 12px); color: var(--ecr-mut, #5a6b73); }

    .guide-main { flex: 1 1 auto; min-width: 0; overflow-y: auto; padding: 20px 24px 28px; }
    .guide-main:focus { outline: none; }
    .guide-module { font-size: 22px; font-weight: 700; color: var(--ecr-ink, #26414c); }
    .guide-lead { max-width: 60rem; font-size: 14px; line-height: 1.5; color: var(--ecr-ink-soft, #26414c); }
    .guide-use {
      flex: 0 0 auto; padding: 6px 14px; border: 0; border-radius: 999px;
      font-size: var(--ecr-font-sm, 12px); font-weight: 600;
      color: var(--ecr-on-primary, #fff); background: var(--ecr-primary, #13455b);
      transition: transform 0.12s ease, box-shadow 0.12s ease;
    }
    .guide-use:hover { box-shadow: 0 4px 12px rgba(16, 32, 48, 0.18); transform: translateY(-1px); }

    /* The example sits on the report's own page colour, on a card, the way it would in the deck. */
    .guide-stage { margin: 16px 0 20px; padding: 14px; border-radius: var(--ecr-radius, 8px); background: var(--ecr-bg, #eef3f5); }
    .guide-stage-head { display: flex; align-items: baseline; gap: 10px; margin-bottom: 10px; }
    .guide-stage-label {
      font-size: var(--ecr-font-micro, 9px); font-weight: 700; letter-spacing: 0.1em;
      text-transform: uppercase; color: var(--ecr-primary, #13455b);
    }
    .guide-stage-note { font-size: var(--ecr-font-xs, 11px); color: var(--ecr-mut, #5a6b73); }
    .guide-card {
      padding: 16px; border: 1px solid var(--ecr-line, #dbe4e8); border-radius: var(--ecr-radius, 8px);
      background: var(--ecr-card, #fff); box-shadow: 0 1px 2px rgba(16, 32, 48, 0.04);
      animation: guide-in 0.2s ease-out;
    }
    .guide-illustration svg { width: 100%; max-height: 260px; }
    @keyframes guide-in { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }

    .guide-facts { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 12px 24px; }
    .guide-facts section { min-width: 0; }
    .guide-facts p, .guide-list { margin: 0; font-size: var(--ecr-font-base, 13px); line-height: 1.5; }
    .guide-h4 {
      display: flex; align-items: center; gap: 6px; margin: 0 0 6px;
      font-size: var(--ecr-font-sm, 12px); font-weight: 700; color: var(--ecr-ink, #26414c);
    }
    .guide-h4 i { color: var(--ecr-primary, #13455b); font-size: 11px; }
    .guide-list { padding-left: 1.1rem; }
    .guide-list li + li { margin-top: 3px; }
    .guide-watch { padding: 10px 12px; border-radius: var(--ecr-radius-sm, 6px); background: var(--ecr-secondary-wash, #fdf6e7); }
    .guide-watch .guide-h4 i { color: var(--ecr-negative, #c53030); }

    .guide-related { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin-top: 20px; }
    .guide-chip {
      padding: 3px 10px; border: 1px solid var(--ecr-line, #dbe4e8); border-radius: 999px;
      font-size: var(--ecr-font-sm, 12px); color: var(--ecr-ink, #26414c); background: var(--ecr-card, #fff);
    }
    .guide-chip:hover, .guide-chip:focus-visible { border-color: var(--ecr-primary, #13455b); color: var(--ecr-primary, #13455b); }
    .guide-note { margin-top: 16px; font-size: var(--ecr-font-sm, 12px); color: var(--ecr-mut, #5a6b73); }

    @media (max-width: 767.98px) {
      .guide { height: auto; max-height: 92vh; }
      .guide-body { flex-direction: column; }
      .guide-nav { flex: 0 0 auto; max-height: 30vh; border-right: 0; border-bottom: 1px solid var(--ecr-line, #dbe4e8); }
    }
    @media (prefers-reduced-motion: reduce) {
      .guide-card { animation: none; }
      .guide-use { transition: none; }
    }
  `],
})
export class ModuleGuideModalComponent implements OnInit, AfterViewInit {
  // Set through the modal's initialState.
  selected: ReportType = 'bar-chart';
  /** The module it was opened from, if any: marked "In use", and the one "Use this module" replaces. */
  current: ReportType | null = null;
  palette: ColorPalette = DEFAULT_COLOR_PALETTE;
  /** Switches the module it was opened from. Absent where there is no module to switch. */
  choose?: (reportType: ReportType) => void;

  @ViewChild('root') private root?: ElementRef<HTMLElement>;
  @ViewChild('main') private main?: ElementRef<HTMLElement>;

  readonly labels = REPORT_BLOCK_LABELS;
  readonly guidance = REPORT_BLOCK_GUIDANCE;
  query = '';
  visibleGroups: GuideGroup[] = [];
  guide: ModuleGuide | null = null;
  block: PageSection | null = null;
  series: ChartSeries[] = [];
  facts: StudyFacts | null = null;
  live = true;

  private readonly groups: GuideGroup[] = groupedReportTypes().map(group => ({
    label: group.label,
    types: group.types.map(option => option.value),
  }));
  private aggregates: AggregateTable | null = null;

  constructor(private modalRef: BsModalRef) { }

  ngOnInit() {
    // The explainer is not in the picker -- it is added from a matrix -- but it is still a module
    // a reader of a deck meets, so it is listed beside the matrix it belongs to.
    const maps = this.groups.find(group => group.types.includes('stage-matrix'));
    if (maps && !maps.types.includes('stage-explainer')) {
      maps.types.splice(maps.types.indexOf('stage-matrix') + 1, 0, 'stage-explainer');
    }
    this.refresh();
    this.render();
  }

  ngAfterViewInit() {
    if (this.root) applyDeckTheme(this.root.nativeElement, this.palette);
  }

  groupOf(type: ReportType): string {
    return this.groups.find(group => group.types.includes(type))?.label ?? '';
  }

  addable(type: ReportType): boolean {
    return !NOT_ADDABLE.has(type);
  }

  /** Narrows the list to modules whose name, summary or uses mention every word searched for. */
  refresh() {
    const words = this.query.toLowerCase().split(/\s+/).filter(Boolean);
    const matches = (type: ReportType) => {
      const guide = MODULE_GUIDE[type];
      const text = [this.labels[type], this.guidance[type], ...guide.bestFor, guide.needs].join(' ').toLowerCase();
      return words.every(word => text.includes(word));
    };
    this.visibleGroups = this.groups
      .map(group => ({ label: group.label, types: group.types.filter(matches) }))
      .filter(group => group.types.length);
  }

  select(type: ReportType) {
    if (type === this.selected) return;
    this.selected = type;
    this.render();
    this.main?.nativeElement.scrollTo({ top: 0 });
  }

  use() {
    this.choose?.(this.selected);
    this.close();
  }

  close() {
    this.modalRef.hide();
  }

  /** Up and down through the list, as a list of options would. */
  onNavKeydown(event: KeyboardEvent) {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const items = Array.from(
      (event.currentTarget as HTMLElement).querySelectorAll<HTMLButtonElement>('[data-guide-item]'));
    if (!items.length) return;
    event.preventDefault();
    const at = items.indexOf(document.activeElement as HTMLButtonElement);
    const next = items[(at + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length];
    next.focus();
    next.click();
  }

  /**
   * The example, worked out exactly as the deck works out a module: the sample's respondents through
   * the same aggregation into the same block component. The filter comparison is the one module that
   * needs a filter to show anything, so it is given the sample's daily buyers.
   */
  private render() {
    this.guide = MODULE_GUIDE[this.selected];
    this.live = hasLiveExample(this.selected);
    if (!this.live) {
      this.block = null;
      this.series = [];
      return;
    }

    const sample = guideSample();
    const { layout, records, variableMap } = sample;
    const keys = Object.keys(variableMap);
    this.aggregates ??= aggregateAll(records, keys, variableMap, layout);
    this.facts ??= studyFacts(records, { completesCount: records.length, terminatesCount: 338 });

    const block = exampleBlock(this.selected);
    let aggregates = this.aggregates;
    let scoped = records;
    if (this.selected === 'filter-comparison') {
      const filter = sampleFilter();
      scoped = applyFilters(records, { [filter.key]: filter.value }, variableMap, layout);
      aggregates = aggregateAll(scoped, keys, variableMap, layout);
    }

    this.block = block;
    this.series = seriesForBlock(block, aggregates, {
      variableMap,
      layout,
      records: scoped,
      overallAggregates: this.aggregates,
    });
  }
}
